import { environment } from '../../../../environments/environment'
import * as OohStatsHelper from '../../ooh/helpers/ooh-stats.helper'

export function safeJsonParse(value: unknown): any {
  if (!value) return undefined
  if (typeof value === 'object') return value
  if (typeof value === 'string') {
    try {
      return JSON.parse(value)
    } catch {
      return undefined
    }
  }
  return undefined
}

export function sum(n: number[]) {
  return n.reduce((a, b) => a + b, 0)
}

interface BuildRankingParams {
  pois: any[]
  currentIndicator: string
  currentIndicatorLabel: string
  campaignName: string
  isVisitsAvailable: boolean
}

export function buildRanking(params: BuildRankingParams) {
  const {
    pois,
    currentIndicator,
    currentIndicatorLabel,
    campaignName,
    isVisitsAvailable,
  } = params

  const list = pois
    .map((row) => {
      const tags =
        typeof row.tags === 'string'
          ? safeJsonParse(row.tags) || []
          : row.tags || []
      const properties =
        typeof row.properties === 'string'
          ? safeJsonParse(row.properties) || {}
          : row.properties || {}
      return {
        ...row,
        original_tags: tags,
        tags: tags.map((t) => t.label || t.name || t).join(', '),
        address: properties.address,
        code: String(row.id).split(':').pop(),
        name: environment.politis ? row.name.replace('Asset', ' ') : row.name,
      }
    })
    .sort((a, b) => b[currentIndicator] - a[currentIndicator])

  const visitorsValues = list.map((p) => p.nvisitors || 0)
  const totalVisitors = sum(visitorsValues)

  const columns = [
    {
      title: 'POIs',
      sortable: true,
      key: 'code',
      numeric: false,
      selected: false,
    },
    {
      title: environment.politis ? 'Description' : 'Address',
      sortable: true,
      key: 'address',
      numeric: false,
      selected: false,
    },
    {
      title: 'Tags',
      sortable: true,
      key: 'tags',
      numeric: false,
      selected: false,
    },
    {
      title: 'Reach',
      sortable: true,
      key: 'nvisitors',
      numeric: true,
      selected: currentIndicator === 'nvisitors',
    },
  ]
  if (isVisitsAvailable) {
    columns.push({
      title: 'Impressions',
      sortable: true,
      key: 'nvisits',
      numeric: true,
      selected: currentIndicator === 'nvisits',
    })
  }

  return {
    list,
    totalVisitors,
    ranking: {
      label: 'name',
      key: currentIndicator,
      data: list.length ? [{ title: campaignName, list }] : [],
      columns,
    },
    legend: [`- ${currentIndicatorLabel}`, `+ ${currentIndicatorLabel}`],
  }
}

interface BuildMobilityStatsParams {
  aggs: any | null
  compareRows: any[] | null
  currentCampaignId: number
  hasCompareDelivery: boolean
  pois?: any[]
}

export function buildVisitorStats(params: BuildMobilityStatsParams) {
  const { aggs, compareRows, currentCampaignId, hasCompareDelivery, pois } =
    params
  const compRef = compareRows?.find((r) => r.delivery_id === currentCampaignId)
  const compComp = compareRows?.find((r) => r.delivery_id !== currentCampaignId)
  const hasCompare = hasCompareDelivery && compRef && compComp

  const poisTrafficSum = pois?.length
    ? pois.reduce((s, p) => s + (Number(p.traffic) || 0), 0)
    : undefined

  return {
    visitorsSum: hasCompare
      ? [compRef.nvisitors_sum, compComp.nvisitors_sum]
      : aggs
      ? aggs.sum
      : undefined,
    visitorsAbs: hasCompare
      ? [compRef.nvisitors_sum, compComp.nvisitors_sum]
      : aggs
      ? aggs.sum
      : undefined,
    visitorsAvg: hasCompare
      ? [compRef.nvisitors_avg, compComp.nvisitors_avg]
      : aggs
      ? aggs.avg
      : undefined,
    traffic: hasCompare
      ? [compRef.traffic_sum, compComp.traffic_sum]
      : poisTrafficSum,
  }
}

export function buildDemographicsStats(
  demo: any,
  compDemo: any | null,
  hasCompare: boolean,
) {
  const refGender = OohStatsHelper.genderDataFromDetail(demo)
  const refAge = OohStatsHelper.ageDataFromDetail(demo, true)
  const refProfile = OohStatsHelper.visitorProfileFromDetail(demo)

  if (compDemo && hasCompare) {
    const compGender = OohStatsHelper.genderDataFromDetail(compDemo)
    const compAge = OohStatsHelper.ageDataFromDetail(compDemo, true)
    const compProfile = OohStatsHelper.visitorProfileFromDetail(compDemo)
    return {
      gender: refGender && compGender ? [refGender, compGender] : refGender,
      age: refAge && compAge ? [refAge, compAge] : refAge,
      visitor_profile:
        refProfile && compProfile ? [refProfile, compProfile] : refProfile,
    }
  }

  return {
    gender: refGender,
    age: refAge,
    visitor_profile: refProfile,
  }
}
